// ---------------------------------------------------------------------------
// "Call us" hours — mirrors backend contact_lead_config.py so the page can
// re-check the hours while it stays open (the config is fetched once per search).
// All times are in the call centre's timezone (fixed UTC offset, e.g. IST = 330).
// ---------------------------------------------------------------------------

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']; // Date#getUTCDay order
const DAY_MS = 24 * 60 * 60 * 1000;
const LOOKAHEAD_DAYS = 8;

const toMinutes = (hhmm) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm).trim());
    if (!m) throw new Error(`bad time ${hhmm}`);
    const h = Number(m[1]);
    const mi = Number(m[2]);
    if (h > 24 || mi > 59 || (h === 24 && mi !== 0)) throw new Error(`bad time ${hhmm}`);
    return h * 60 + mi;
};

const cleanWindows = (windows) =>
    (Array.isArray(windows) ? windows : [])
        .map(w => ({ s: toMinutes(w.start), e: toMinutes(w.end) }))
        .filter(w => w.s !== w.e)
        .map(w => ({ ...w, overnight: w.e < w.s }));

// A "local day" is a Date whose UTC fields are the call centre's wall clock
const localNow = (now, offsetMin) => new Date(now.getTime() + offsetMin * 60000);
const dateKey = (d) => d.toISOString().slice(0, 10);

const windowsFor = (callUs, localDay) => {
    const key = dateKey(localDay);
    const overrides = callUs.overrides || {};
    if (Object.prototype.hasOwnProperty.call(overrides, key)) return cleanWindows(overrides[key]);
    return cleanWindows((callUs.hours || {})[DAY_KEYS[localDay.getUTCDay()]]);
};

/**
 * @param callUs  config.call_us from the backend
 * @param now     Date (defaults to now)
 * @returns { open, closes: {dayOffset, minutes} | null, nextOpen: {dayOffset, minutes} | null }
 */
export const getCallStatus = (callUs, now = new Date()) => {
    const fallback = { open: !!callUs?.available_now, closes: null, nextOpen: null };
    if (!callUs || !callUs.hours || Object.keys(callUs.hours).length === 0) return fallback;

    try {
        const offset = Number(callUs.utc_offset_minutes ?? 330);
        const today = localNow(now, offset);
        const minute = today.getUTCHours() * 60 + today.getUTCMinutes();
        const yesterday = new Date(today.getTime() - DAY_MS);

        for (const w of windowsFor(callUs, today)) {
            if ((!w.overnight && w.s <= minute && minute < w.e) || (w.overnight && minute >= w.s)) {
                return { open: true, closes: { dayOffset: w.overnight ? 1 : 0, minutes: w.e }, nextOpen: null };
            }
        }
        for (const w of windowsFor(callUs, yesterday)) {
            if (w.overnight && minute < w.e) {
                return { open: true, closes: { dayOffset: 0, minutes: w.e }, nextOpen: null };
            }
        }
        for (let i = 0; i < LOOKAHEAD_DAYS; i++) {
            const day = new Date(today.getTime() + i * DAY_MS);
            const starts = windowsFor(callUs, day)
                .map(w => w.s)
                .filter(s => i > 0 || s > minute)
                .sort((a, b) => a - b);
            if (starts.length) return { open: false, closes: null, nextOpen: { dayOffset: i, minutes: starts[0] } };
        }
        return { open: false, closes: null, nextOpen: null };
    } catch (e) {
        console.error('[callHours] invalid hours, using backend flag', e);
        return fallback;
    }
};

// 540 → "9 AM", 570 → "9:30 AM", 1440 → "12 AM"
export const formatMinutes = (minutes) => {
    const m = minutes % (24 * 60);
    const h24 = Math.floor(m / 60);
    const mi = m % 60;
    const suffix = h24 < 12 ? 'AM' : 'PM';
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return mi ? `${h12}:${String(mi).padStart(2, '0')} ${suffix}` : `${h12} ${suffix}`;
};

// Closing time: 1440 / 0 → "midnight"
export const formatCloseTime = (minutes) => (minutes % (24 * 60) === 0 ? 'midnight' : formatMinutes(minutes));

// "today at 9 AM" / "tomorrow at 10 AM" / "on Mon at 9 AM"
export const describeNextOpen = (nextOpen, callUs, now = new Date()) => {
    if (!nextOpen) return '';
    const time = formatMinutes(nextOpen.minutes);
    if (nextOpen.dayOffset === 0) return `today at ${time}`;
    if (nextOpen.dayOffset === 1) return `tomorrow at ${time}`;
    const offset = Number(callUs?.utc_offset_minutes ?? 330);
    const day = new Date(localNow(now, offset).getTime() + nextOpen.dayOffset * DAY_MS);
    const name = day.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
    return `on ${name} at ${time}`;
};
